// The headline row: the same five answers in every stage, in one panel
// apps/smart-catalog/frontend/src/components/portfolio/HeadlineCard.tsx
//
// One white panel cut into five columns by hairlines. Each column answers one
// question with a ring of every product in view, in the catalog-health blues
// (designKit HEAT): a slice per reason, the worst darkest and the next ones
// lighter, the palest for missing set-up, the rest left empty; the products
// that need someone in the middle, and the reasons beside it, one line each,
// their counts in red or amber so a problem still stands out.
// The server counts every product once, under its worst reason (bom-v2
// portfolio.service.ts headline()). The cost column rings the savings instead:
// ready against pending, the sum in the middle, the value above them.

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import type { HeadlineCard as Card, HeadlineReason } from '../../services/portfolioService';
import { captionSx, T } from './PortfolioParts';

const ICON: Record<Card['key'], React.ReactNode> = {
  schedule: <AccessTimeIcon sx={{ fontSize: 16 }} />,
  supply: <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />,
  changes: <SwapHorizIcon sx={{ fontSize: 16 }} />,
  package: <FactCheckOutlinedIcon sx={{ fontSize: 16 }} />,
  cost: <AttachMoneyIcon sx={{ fontSize: 16 }} />,
};
/** The catalog-health blues, darkest first (designKit HEAT). */
const BLUES = ['#2563eb', '#5b8def', '#93b4f5'];
/** Missing set-up (no plan, never checked): the palest slice. */
const SETUP = '#c7d7fe';
/** A reason's slice: by its place in the card (worst first), set-up always palest. */
const slice = (r: HeadlineReason, i: number) => (r.tone === 'none' ? SETUP : BLUES[Math.min(i, BLUES.length - 1)]);
/** Counts in text keep the status colours. */
const COUNT: Record<HeadlineReason['tone'], string> = { red: '#b42318', amber: '#b45309', none: T.ink2 };
const TRACK = '#eef4ff';
const HAIRLINE = '#eef1f5';
const SAVE = { ready: BLUES[0], pending: BLUES[2], text: '#1d4ed8' };

const SIZE = 84;
const STROKE = 9;
const R = SIZE / 2 - STROKE / 2 - 0.5;
const C = 2 * Math.PI * R;

/** A ring cut into slices of one total, clockwise from the top, a 2px gap after each slice. */
const Ring: React.FC<{ slices: Array<{ n: number; color: string }>; total: number; label: string; children: React.ReactNode }> = ({ slices, total, label, children }) => {
  let start = 0;
  const arcs = slices.map((s) => {
    const len = total > 0 ? (s.n / total) * C : 0;
    const shown = len > 3 ? len - 2 : len;
    const arc = { color: s.color, dash: `${shown} ${C}`, offset: -start };
    start += len;
    return arc;
  });
  const c = SIZE / 2;
  return (
    <Box sx={{ position: 'relative', width: SIZE, height: SIZE, flexShrink: 0 }}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label={label}>
        <circle cx={c} cy={c} r={R} fill="none" stroke={TRACK} strokeWidth={STROKE} />
        {arcs.map((a, i) =>
          a.dash.startsWith('0 ') ? null : (
            <circle key={i} cx={c} cy={c} r={R} fill="none" stroke={a.color} strokeWidth={STROKE} strokeDasharray={a.dash} strokeDashoffset={a.offset} transform={`rotate(-90 ${c} ${c})`} />
          ),
        )}
      </svg>
      <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0 }}>
        {children}
      </Stack>
    </Box>
  );
};

/** The ring's middle: a number (smaller when it is long money) and a word under it. */
const Middle: React.FC<{ value: string; under: string }> = ({ value, under }) => (
  <>
    <Typography sx={{ fontFamily: T.mono, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.02em', color: T.ink, fontSize: value.length <= 4 ? 20 : value.length === 5 ? 16 : 14 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 10.5, color: T.muted }}>{under}</Typography>
  </>
);

/** One line beside the ring: a colour square (or an empty slot), the label, the count. */
const Line: React.FC<{ color?: string; label: string; count: string; countColor: string; labelColor?: string }> = ({ color, label, count, countColor, labelColor = T.ink2 }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '2px', flexShrink: 0, bgcolor: color ?? 'transparent' }} />
    <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 13, color: labelColor }}>{label}</Typography>
    <Typography sx={{ flexShrink: 0, fontFamily: T.mono, fontSize: 13, fontWeight: 600, color: countColor }}>{count}</Typography>
  </Stack>
);

const Column: React.FC<{ card: Card }> = ({ card }) => {
  let ring: React.ReactNode;
  let lines: React.ReactNode;
  if (card.money) {
    const m = card.money;
    // An older server sends no sum or share: the ring then splits by the value fractions.
    const share = m.readyShare ?? (m.readyFrac + m.pendingFrac > 0 ? m.readyFrac / (m.readyFrac + m.pendingFrac) : 0);
    const any = m.readyFrac + m.pendingFrac > 0 || (m.readyShare ?? 0) > 0;
    ring = (
      <Ring slices={any ? [{ n: share, color: SAVE.ready }, { n: 1 - share, color: SAVE.pending }] : []} total={1} label={`Savings ${m.savings ?? ''}: ${m.ready} ready, ${m.pending} pending. Value ${m.value}`}>
        <Middle value={m.savings ?? m.ready} under="savings" />
      </Ring>
    );
    lines = (
      <>
        <Line label="Value" count={m.value} countColor={T.ink} />
        <Line color={SAVE.ready} label="Ready" count={m.ready} countColor={SAVE.text} />
        <Line color={SAVE.pending} label="Pending" count={`+${m.pending}`} countColor={SAVE.text} />
      </>
    );
  } else {
    const reasons = card.reasons ?? [];
    const of = card.of ?? 0;
    ring = (
      <Ring
        slices={reasons.map((r, i) => ({ n: r.count, color: slice(r, i) }))}
        total={of}
        label={`${card.label}: ${card.need ?? 0} of ${of} need attention. ${reasons.map((r) => `${r.label} ${r.count}`).join(', ')}`}
      >
        <Middle value={String(card.need ?? 0)} under={`of ${of}`} />
      </Ring>
    );
    lines = reasons.map((r, i) => (
      <Line key={r.key} color={slice(r, i)} label={r.label} count={String(r.count)} countColor={r.count ? COUNT[r.tone] : T.muted} labelColor={r.tone === 'none' ? T.muted : T.ink2} />
    ));
  }
  return (
    <Stack spacing={2} sx={{ p: '20px 22px', bgcolor: '#fff', minWidth: 0, gridColumn: card.key === 'cost' ? { sm: 'span 2', lg: 'auto' } : undefined }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ color: T.muted }}>
        {ICON[card.key]}
        <Typography sx={captionSx}>{card.label}</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={2.25} sx={{ minWidth: 0 }}>
        {ring}
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          {lines}
        </Stack>
      </Stack>
    </Stack>
  );
};

/** The five answers in one panel; hairlines between the columns, and between rows when they wrap. */
export const HeadlinePanel: React.FC<{ cards: Card[] }> = ({ cards }) => (
  <Box
    component="section"
    aria-label="Portfolio health"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: `repeat(${Math.max(cards.length, 1)}, minmax(0, 1fr))` },
      gap: '1px',
      bgcolor: HAIRLINE,
      border: `1px solid ${T.line}`,
      borderRadius: '16px',
      overflow: 'hidden',
    }}
  >
    {cards.map((c) => (
      <Column key={c.key} card={c} />
    ))}
  </Box>
);

export default HeadlinePanel;
